import { useCallback, useEffect, useRef, useState } from 'react';
import type { MyOCSDK, UserAcceptance } from '@cx-sdk/myoc';
import { type ILabelActions } from '@cx-web/feature-i18n';
import { REPUB_CONFIG } from '@cx-web/repub-config';
import { getMyOcBasePath, getRepubMetaFromRepub } from '@cx-web/utils';

import { type Repub } from '@cx-modules/constants';

import { useAuthModal } from '../auth-modal-provider';
import { getActiveAuthModalStore, notifyAuthLogin, VIEW_FORGOT_PASSWORD, VIEW_SIGNUP } from '../store/auth-modal-store';
import MyOCLogin2018Modal from './MyOCLogin2018Modal';
import { type AuthBanner, type MyOCLogin2018View } from './types';

export interface MyOCLogin2018ContainerProps {
  /** MyOC API host the browser SDK calls (config `MYOC_LOGIN.WIDGET.API_HOST`). */
  apiHost: string;
  /** Repub API key — already shipped to the client today by the legacy widget config. */
  apiKey: string;
  deviceId: string;
  labelActions: ILabelActions;
  /** Drives the repub-specific policy links shown in the forced-compliance gate. */
  repub: Repub;
  /** Base URL the modal's hero-panel artwork is served from (`${resourcesUrl}/assets/img/`). */
  resourcesUrl: string;
}

// Maps the framework-agnostic store view onto the composite's view set. The store's verify-* views are
// native-modal concepts the legacy OC widget never showed, so they fall back to login.
const toCompositeView = (storeView: string): MyOCLogin2018View => {
  if (storeView === VIEW_SIGNUP) return 'signup';
  if (storeView === VIEW_FORGOT_PASSWORD) return 'reset-password';
  return 'login';
};

/**
 * Posts the committed session to cx-web so the MyOC session cookie is set (legacy handleAuthSuccess).
 *
 * The session endpoint moved from RR7 atlas (`/api/cx/myoc/session`) to the repub-prefixed
 * myoddschecker namespace (`/{repub}/myoddschecker/session`) in NGF-6477, so the caller must thread
 * its `repub` in to build the path.
 */
const commitSession = async (
  repub: Repub,
  user: {
    deviceId: string;
    email: string;
    firstName: string;
    token: string;
    userId: string;
  },
): Promise<void> => {
  try {
    const response = await fetch(`${getMyOcBasePath(repub)}/session`, {
      body: JSON.stringify({ action: 'commit', user }),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    });
    if (!response.ok) {
      // Non-fatal: the SDK login already succeeded so we still let the user in; only the server-set
      // session cookie is missing until the next full load. Log it for diagnosis rather than swallowing
      // silently (the legacy widget console.error'd here too — handleAuthSuccess in login-view.tsx).
      // eslint-disable-next-line no-console
      console.error(`MyOC session commit failed: ${response.status}`);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('MyOC session commit request failed', error);
  }
};

/**
 * Drives the ported `MyOCLogin2018` composite from the framework-agnostic auth-modal store, performing auth
 * via the browser `@cx-sdk/myoc` client. Replaces the remote `myoc-login` bundle (`useMyOCLoginInit`)
 * and the `LegacyAuthModalBridge` (NGF-6371). Mounted once inside `AuthModalProvider` in each app that
 * renders the OC auth UI (RR7 atlas, atlas-astro). Renders nothing until the modal is opened.
 */
export const MyOCLogin2018Container = ({
  apiHost,
  apiKey,
  deviceId,
  labelActions,
  repub,
  resourcesUrl,
}: MyOCLogin2018ContainerProps): React.JSX.Element | null => {
  const { close, isOpen, prefillEmail, view: storeView } = useAuthModal();

  // The SDK module is loaded lazily (dynamic import) on first auth action, NOT eagerly on mount. This
  // container renders in the page chrome on every route but the modal is rarely opened, so keeping the
  // @cx-sdk/myoc module (which inlines prom-client) out of the route chunk avoids both the page-wide
  // hydration crash (see the polyfill note above) and shipping the SDK on every page load. The
  // constructor also Zod-validates apiHost/apiKey/deviceId and throws synchronously when they're absent,
  // so building it at mount would let a missing/invalid auth config take down the whole page chrome.
  const sdkRef = useRef<MyOCSDK | null>(null);
  const getSdk = useCallback(async (): Promise<MyOCSDK> => {
    if (!sdkRef.current) {
      // TEMPORARY HACK (see sdk-process-polyfill.ts for the removal conditions): must run before the SDK
      // evaluates, because the @cx-sdk/myoc browser bundle reads Node `process` at module load. Imported
      // dynamically — and awaited — right before the SDK so the bundler emits and runs it first. A static
      // side-effect import was tree-shaken out of the production build (root package.json `sideEffects:
      // false`, no consumed exports), leaving `process` undefined → the SDK chunk threw on eval → the
      // dynamic import below rejected → login failed silently in prod only. An explicit `import()` is a
      // runtime statement DCE cannot drop, so the shim is guaranteed present and ordered ahead of the SDK.
      await import('./sdk-process-polyfill');
      const { MyOCSDK } = await import('@cx-sdk/myoc');
      sdkRef.current = new MyOCSDK({ apiHost, apiKey, deviceId });
    }
    return sdkRef.current;
  }, [apiHost, apiKey, deviceId]);

  const { repubPath } = getRepubMetaFromRepub(repub);
  const authConfig = REPUB_CONFIG[repub]?.plus?.auth;
  const termsHref = authConfig?.termsAndConditionsUrl(repubPath) ?? '#';
  const privacyPolicyHref = authConfig?.privacyPolicyUrl(repubPath) ?? '#';

  const [view, setView] = useState<MyOCLogin2018View>('login');
  const [busy, setBusy] = useState(false);
  const [loginBanner, setLoginBanner] = useState<AuthBanner | null>(null);
  const [signupBanner, setSignupBanner] = useState<AuthBanner | null>(null);
  const [resetSubmitted, setResetSubmitted] = useState(false);
  const [doneJourney, setDoneJourney] = useState<'signup' | 'preferences'>('signup');

  const lastEmailRef = useRef('');
  const pendingAcceptanceRef = useRef<UserAcceptance | null>(null);
  const wasOpenRef = useRef(false);

  // On each fresh open, seed the view from the store and clear any state from a previous session. A
  // view change while already open (switch/forgot) is driven locally and must not reset here.
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      wasOpenRef.current = true;
      setView(toCompositeView(storeView));
      setBusy(false);
      setLoginBanner(null);
      setSignupBanner(null);
      setResetSubmitted(false);
    } else if (!isOpen && wasOpenRef.current) {
      wasOpenRef.current = false;
    }
  }, [isOpen, storeView]);

  // Lock background scroll while the modal is open. Keyed on isOpen with a cleanup return so the prior
  // overflow is restored both when the modal closes AND if the container unmounts while still open (a
  // route change mid-modal) — the latter is why this is its own effect, not a branch of the open/close
  // effect above. Replaces a dead `body_fixed` class toggle: no stylesheet defined that class, so the
  // ported widget never actually locked scroll.
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const finishLogin = useCallback((): void => {
    const store = getActiveAuthModalStore();
    if (store) notifyAuthLogin(store);
    close();
  }, [close]);

  const onLoginSubmit = useCallback(
    async ({ email, password, rememberMe }: { email: string; password: string; rememberMe: boolean }) => {
      lastEmailRef.current = email;
      setLoginBanner(null);
      setBusy(true);
      try {
        const res = await (await getSdk()).login({ email, password, remember: rememberMe });

        if (!res.success) {
          if (res.messageCode === 'AL102') {
            setLoginBanner({
              message: labelActions.get('AUTH_ACCOUNT_NOT_VERIFIED'),
              // Resend has its own try/catch: the SDK call can reject (network), and without it the banner
              // would stay stuck in the verification state with no feedback that the resend failed.
              onResend: async () => {
                try {
                  await (await getSdk()).resendVerificationEmail(lastEmailRef.current);
                  setLoginBanner({
                    message: labelActions.get('AUTH_EMAIL_RESENT'),
                    title: labelActions.get('AUTH_LOGIN_SUCCESS'),
                    type: 'success',
                  });
                } catch (error) {
                  // eslint-disable-next-line no-console
                  console.error('MyOC resend verification email failed', error);
                  setLoginBanner({
                    message: labelActions.get('CX_ERROR_PAGE_DESCRIPTION'),
                    title: labelActions.get('AUTH_LOGIN_FAILED'),
                    type: 'error',
                  });
                }
              },
              title: labelActions.get('AUTH_LOGIN_FAILED'),
              type: 'verification',
            });
            return;
          }
          setLoginBanner({
            message: res.messageCode === 'AL101' ? labelActions.get('AUTH_ACCOUNT_NOT_RECOGNISED') : res.message,
            title: labelActions.get('AUTH_LOGIN_FAILED'),
            type: 'error',
          });
          return;
        }

        await commitSession(repub, {
          deviceId: res.deviceId,
          email,
          firstName: res.screenName,
          token: res.token,
          userId: res.userId,
        });

        // Mirror the legacy gate (login-view.tsx handleAuthSuccess): the compliance view only appears when
        // termsAndConditions is NOT accepted. Privacy-policy and marketing being unaccepted never open the
        // modal on their own (legacy's PRIVACY_POLICY/MY_PREFERENCES branches were commented out) — privacy
        // only escalates the terms gate to the INTRODUCTION ("Terms of service update") copy. Gating on
        // privacy here made the modal reappear on every login for users with terms:true, privacy:false.
        if (res.userAcceptance.termsAndConditions) {
          finishLogin();
          return;
        }
        pendingAcceptanceRef.current = res.userAcceptance;
        setView('compliance');
      } catch (error) {
        // A rejected SDK/network call would otherwise leave busy stuck true (infinite spinner). Surface a
        // generic error and let the finally clear busy so the user can retry. Log the real cause too: the
        // generic banner is intentionally vague, so without this a build-only failure (e.g. the SDK chunk
        // throwing on eval) is invisible in the console — exactly what masked the NGF-6371 polyfill DCE bug.
        // eslint-disable-next-line no-console
        console.error('MyOC login failed', error);
        setLoginBanner({
          message: labelActions.get('CX_ERROR_PAGE_DESCRIPTION'),
          title: labelActions.get('AUTH_LOGIN_FAILED'),
          type: 'error',
        });
      } finally {
        setBusy(false);
      }
    },
    [finishLogin, getSdk, labelActions, repub],
  );

  const onSignupSubmit = useCallback(
    async ({ email, marketingConsent, password }: { email: string; marketingConsent: boolean; password: string }) => {
      lastEmailRef.current = email;
      setSignupBanner(null);
      setBusy(true);
      try {
        const res = await (
          await getSdk()
        ).register({
          communications: marketingConsent,
          email,
          firstName: '',
          password,
          surname: '',
        });

        if (!res.success) {
          setSignupBanner({
            message: res.message,
            title: labelActions.get('AUTH_SIGNUP_FAILED'),
            type: 'error',
          });
          return;
        }
        setDoneJourney('signup');
        setView('done');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('MyOC signup failed', error);
        setSignupBanner({
          message: labelActions.get('CX_ERROR_PAGE_DESCRIPTION'),
          title: labelActions.get('AUTH_SIGNUP_FAILED'),
          type: 'error',
        });
      } finally {
        setBusy(false);
      }
    },
    [getSdk, labelActions],
  );

  const onResetSubmit = useCallback(
    async (email: string) => {
      setBusy(true);
      try {
        await (await getSdk()).forgotPassword(email);
        setResetSubmitted(true);
      } finally {
        setBusy(false);
      }
    },
    [getSdk],
  );

  const onComplianceAccept = useCallback(async () => {
    setBusy(true);
    try {
      await (
        await getSdk()
      ).updateProfilePreference({
        marketingPreferences: pendingAcceptanceRef.current?.marketingPreferences ?? null,
        privacyPolicy: true,
        termsAndConditions: true,
      });
      finishLogin();
    } finally {
      setBusy(false);
    }
  }, [finishLogin, getSdk]);

  // Close handler for the modal frame (overlay click, ✕, Escape). On the compliance view the session is
  // already committed server-side (commitSession runs in onLoginSubmit before setView('compliance')), so
  // closing must bump the login nonce — otherwise the optimistic header keeps showing "Sign In" until a
  // full reload. finishLogin() only signals login-happened; it does NOT accept T&C, so the gate re-appears
  // on the next login (no implicit GDPR acceptance). Every other view closes normally.
  const onCloseModal = useCallback(() => {
    if (view === 'compliance') {
      finishLogin();
      return;
    }
    close();
  }, [close, finishLogin, view]);

  const onSwitchToLogin = useCallback(() => {
    setLoginBanner(null);
    setResetSubmitted(false);
    setView('login');
  }, []);

  const onSwitchToSignup = useCallback(() => {
    setSignupBanner(null);
    setView('signup');
  }, []);

  if (!isOpen) return null;

  return (
    <MyOCLogin2018Modal
      complianceLoading={busy}
      defaultEmail={prefillEmail}
      doneJourney={doneJourney}
      labelActions={labelActions}
      loginBanner={loginBanner}
      loginLoading={busy}
      onClose={onCloseModal}
      onComplianceAccept={onComplianceAccept}
      onDoneContinue={close}
      onForgotPassword={() => setView('reset-password')}
      onLoginSubmit={onLoginSubmit}
      onResetSubmit={onResetSubmit}
      onSignupSubmit={onSignupSubmit}
      onSwitchToLogin={onSwitchToLogin}
      onSwitchToSignup={onSwitchToSignup}
      privacyPolicyHref={privacyPolicyHref}
      resetLoading={busy}
      resetSubmitted={resetSubmitted}
      resourcesUrl={resourcesUrl}
      signupBanner={signupBanner}
      signupLoading={busy}
      termsHref={termsHref}
      view={view}
    />
  );
};

MyOCLogin2018Container.displayName = 'MyOCLogin2018Container';
